'use client';

import Typeahead from '../../../atoms/Typeahead/Typeahead';
import { getValidationResponse } from '../utils';
import { useFormContext } from '../FormContext';
import type { TypeaheadField } from '../types';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  FieldValues,
  FieldErrors,
} from 'react-hook-form';

export type TypeaheadControlProps = {
  field: TypeaheadField;
  theme: 'dark' | 'light';
  direction: 'ltr' | 'rtl';
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  errors: FieldErrors<FieldValues>;
  trigger: UseFormTrigger<FieldValues>;
};

const TypeaheadControl = ({
  field,
  theme,
  direction,
  register,
  setValue,
  watch,
  errors,
  trigger,
}: TypeaheadControlProps) => {
  const { disabled: formDisabled } = useFormContext();

  const depField = field.disabledDependency;
  const depValue = depField ? watch(depField.name) : undefined;
  const isDisabledByDependency = depField
    ? depField.value !== undefined
      ? depValue === depField.value
      : !!depValue
    : false;

  const isDisabled = field.disabled || isDisabledByDependency || formDisabled;

  // Register the field for form validation
  register(field.name, {
    validate: (value) => {
      if (field.required && !value) {
        return field.validationMessages?.required || 'This field is required';
      }
      if (field.validate) {
        return field.validate(value);
      }
      return true;
    },
  });

  const validationResponse = getValidationResponse({ field: field.name, errors });

  return (
    <Typeahead
      key={`form_field_${field.name}`}
      name={field.name}
      label={field.label}
      placeholder={field.placeholder}
      helperText={field.helperText}
      theme={theme}
      direction={direction}
      options={field.options}
      fetchOptions={field.fetchOptions}
      value={watch(field.name) ?? ''}
      disabled={isDisabled}
      required={field.required}
      minQueryLength={field.minQueryLength}
      debounceMs={field.debounceMs}
      loadingText={field.loadingText}
      noResultsText={field.noResultsText}
      onSelect={(value) => {
        setValue(field.name, value, { shouldValidate: true, shouldDirty: true });
        field.onSelect?.(value);
      }}
      onChange={(value) => {
        if (value !== '') {
          return;
        }

        setValue(field.name, '', {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
        void trigger(field.name);
      }}
      onBlur={() => trigger(field.name)}
      validationResponse={validationResponse}
    />
  );
};

export default TypeaheadControl;
